// Registrer trykk (/create-service/:customerNumber), i startsidens stil (planer/ny-kundesamhandling-plan.md).
// Nås fra «Registrer trykk» på startsiden (velg kunde først) og fra kundekortet. Bare tekstiltrykk
// (vinnsel 30.10): sykkel, ski og skøyteslip registreres i butikkmodus og er ikke her lenger.
// «Send SMS med kundelenke» (av som standard): kunden ser trykket og kan sende melding (/kundetrykk/…).
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Tilbake, Sidetopp, Tekstfelt, Valgfelt, knappPrimar } from './ny-kundesamhandling/Skjema';
import Ikon from './ny-kundesamhandling/Ikon';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TRYKK = 'Tekstiltrykking';

function CreateService() {
  const { customerNumber } = useParams();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [kunde, setKunde] = useState(null);
  const [toyBetalt, setToyBetalt] = useState(false);
  const [trykkBetalt, setTrykkBetalt] = useState(false);
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState('');
  const [sendLenke, setSendLenke] = useState(false);
  const [formData, setFormData] = useState({
    beskrivelse: '',
    Varemerke: '',
    Produkt: '',
    Størrelse: '',
    Farge: '',
    ansatt: Cookies.get('selectedEmployee') || '',
    butikkid: Cookies.get('butikkid') || '',
    kundeid: parseInt(customerNumber, 10),
    serviceid: '',
    ferdigDato: '',
    ferdigTid: '',
  });

  useEffect(() => {
    const fetchLastServiceId = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/services/last-service-id`);
        const data = await response.json();
        const lastServiceId = parseInt(data.lastServiceId, 10);
        setFormData((prev) => ({ ...prev, serviceid: isNaN(lastServiceId) ? 1 : lastServiceId + 1 }));
      } catch (error) {
        console.error('Feil ved henting av siste service-ID:', error);
      }
    };
    const fetchEmployees = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/employees`);
        setEmployees(await response.json());
      } catch (error) {
        console.error('Feil ved henting av ansatte:', error);
      }
    };
    // Kunden trykket gjelder, så den ansatte ser at det er riktig kunde (som CreateOrder).
    const fetchKunde = async () => {
      try {
        const nr = parseInt(customerNumber, 10);
        const response = await fetch(`${API_BASE_URL}/customers?customerNumber=${encodeURIComponent(nr)}`);
        if (!response.ok) return;
        const data = await response.json();
        setKunde((Array.isArray(data) ? data : []).find((c) => c.customerNumber === nr) || null);
      } catch (error) {
        console.error('Feil ved henting av kunden:', error);
      }
    };
    fetchLastServiceId();
    fetchEmployees();
    fetchKunde();
  }, [customerNumber]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lagrer) return;
    if (!formData.ansatt) { setFeil('Velg hvilken ansatt som registrerer.'); return; }
    setLagrer(true);
    setFeil('');

    // Dato og klokkeslett lagres som før (ISO, klokkeslettet slik det er skrevet inn).
    const ferdigDatoISO = formData.ferdigDato ? `${formData.ferdigDato}T${formData.ferdigTid || '00:00'}:00.000Z` : null;
    const newService = {
      ...formData,
      // Betalingsstatus for tøy og trykk (spurt om i et vindu før 28.10, nå i skjemaet).
      toyBetalt,
      trykkBetalt,
      Beskrivelse: formData.beskrivelse,
      serviceid: formData.serviceid,
      butikkid: Cookies.get('butikkid') || formData.butikkid,
      registrertDato: new Date().toISOString(),
      ferdigDato: ferdigDatoISO,
      status: 'Aktiv',
      endretdato: '',
      servicetype: TRYKK,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newService),
      });
      if (response.ok) {
        const addedService = await response.json();
        let smsLenke = null;
        if (sendLenke && kunde?.phoneNumber) {
          try {
            const r = await fetch(`${API_BASE_URL}/services/${addedService._id}/send-lenke`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ansatt: formData.ansatt }),
            });
            smsLenke = r.ok ? 'sendt' : 'feil';
          } catch {
            smsLenke = 'feil';
          }
        }
        navigate(`/service-details-club/${addedService._id}`, { state: { smsLenke } });
        return;
      }
      console.error('Feil ved registrering av tjeneste:', response.status);
      setFeil('Klarte ikke å registrere trykket. Prøv igjen.');
    } catch (error) {
      console.error('Feil ved kommunikasjon med serveren:', error);
      setFeil('Fikk ikke kontakt med serveren. Prøv igjen.');
    }
    setLagrer(false);
  };

  const kundeNavn = kunde ? [kunde.firstName, kunde.lastName].filter(Boolean).join(' ') : '';

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      {kunde ? <Tilbake til={`/customer-details/${kunde._id}`}>Tilbake til kundekortet</Tilbake> : <Tilbake />}

      <Sidetopp
        ikon="genser"
        farge="trykk"
        tittel="Registrer trykk"
        undertittel={formData.serviceid ? `Trykknr. ${formData.serviceid}` : 'Navn eller logo på klær'}
      />

      {kunde && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-sky-200 bg-sky-50 p-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-700 text-white">
            <Ikon navn="person" className="w-5 h-5" />
          </span>
          <p className="min-w-0 flex-1 basis-48 text-slate-800">
            <span className="block text-sm text-slate-600">Trykket gjelder</span>
            <span className="block font-semibold [overflow-wrap:anywhere]">{kundeNavn || 'Uten navn'}</span>
            <span className="block font-mono text-sm">{kunde.normalizedPhone || kunde.phoneNumber}</span>
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-slate-700">Felt med <span className="text-red-700">*</span> må fylles ut.</p>

        <div className="grid gap-3 sm:grid-cols-2">
          <Tekstfelt id="nt-merke" etikett="Varemerke" name="Varemerke" value={formData.Varemerke} onChange={handleChange} required />
          <Tekstfelt id="nt-produkt" etikett="Produkt" hjelp="F.eks. treningsjakke, t-skjorte, bag" name="Produkt" value={formData.Produkt} onChange={handleChange} required />
          <Tekstfelt id="nt-str" etikett="Størrelse/lengde (valgfritt)" name="Størrelse" value={formData.Størrelse} onChange={handleChange} />
          <Tekstfelt id="nt-farge" etikett="Farge (valgfritt)" name="Farge" value={formData.Farge} onChange={handleChange} />
        </div>

        <Tekstfelt
          id="nt-beskrivelse"
          etikett="Hva skal trykkes?"
          hjelp="Navn, nummer, logo og plassering. Skriv utfyllende. Internt: kunden ser ikke dette."
          flerlinjet
          name="beskrivelse"
          value={formData.beskrivelse}
          onChange={handleChange}
          required
        />
        <p className="-mt-3 text-sm text-slate-700">
          Usikker på plasseringen for en klubb? Se{' '}
          <Link to="/trykkeguide" target="_blank" rel="noopener" className="font-semibold text-indigo-700 underline underline-offset-2">
            trykkeguiden<span className="sr-only"> (åpnes i ny fane)</span>
          </Link>.
        </p>

        <Valgfelt id="nt-ansatt" etikett="Ansatt" name="ansatt" value={formData.ansatt} onChange={handleChange} required>
          <option value="">Velg en ansatt</option>
          {employees.map((employee) => (
            <option key={employee._id} value={employee.navn}>{employee.navn}</option>
          ))}
        </Valgfelt>

        <fieldset className="min-w-0 space-y-2">
          <legend className="font-semibold text-slate-800">Lovet ferdig (valgfritt)</legend>
          <p id="nt-ferdig-hjelp" className="text-sm text-slate-700">
            Lov bare en dato du vet det er kapasitet til.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <label htmlFor="nt-dato" className="mb-1 block font-medium text-slate-800">Dato</label>
              <input id="nt-dato" type="date" name="ferdigDato" value={formData.ferdigDato} onChange={handleChange}
                aria-describedby="nt-ferdig-hjelp" className="w-full rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 text-base focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30" />
            </div>
            <div className="min-w-0">
              <label htmlFor="nt-tid" className="mb-1 block font-medium text-slate-800">Klokkeslett</label>
              <input id="nt-tid" type="time" name="ferdigTid" value={formData.ferdigTid} onChange={handleChange}
                className="w-full rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 text-base focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30" />
            </div>
          </div>
        </fieldset>

        <fieldset className="min-w-0 space-y-2 rounded-2xl border border-amber-200 bg-amber-50 p-3">
            <legend className="px-1 font-semibold text-slate-900">Betaling</legend>
            <label className="flex min-h-[2.25rem] cursor-pointer items-center gap-2 text-slate-800">
              <input type="checkbox" checked={toyBetalt} onChange={(e) => setToyBetalt(e.target.checked)} className="h-5 w-5" />
              Tøyet er betalt
            </label>
            <label className="flex min-h-[2.25rem] cursor-pointer items-center gap-2 text-slate-800">
              <input type="checkbox" checked={trykkBetalt} onChange={(e) => setTrykkBetalt(e.target.checked)} className="h-5 w-5" />
              Trykket er betalt
            </label>
          </fieldset>

        <div className="rounded-xl border border-slate-200 bg-white px-3 py-1.5">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1 h-5 w-5"
              checked={sendLenke && !!kunde?.phoneNumber}
              disabled={!kunde?.phoneNumber}
              onChange={(e) => setSendLenke(e.target.checked)}
              aria-describedby="nt-lenke-hjelp"
            />
            <span>
              <span className="font-medium">Send SMS med kundelenke</span>
              <span id="nt-lenke-hjelp" className="block text-sm text-slate-600">
                {kunde?.phoneNumber
                  ? 'Kunden får en lenke der hen ser varen og status, og kan sende dere melding.'
                  : 'Kunden har ikke telefonnummer.'}
              </span>
            </span>
          </label>
        </div>

        {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

        <button type="submit" disabled={lagrer} className={`${knappPrimar} w-full sm:w-auto`}>
          <Ikon navn="hake" className="w-6 h-6" />
          {lagrer ? 'Registrerer …' : 'Registrer trykk'}
        </button>
      </form>
    </div>
  );
}

export default CreateService;
